import animateTag from "@client/content/hocs/animate";
import Icon from "@client/controls/Icon/component";
import Modal from "@client/controls/Modal/container";
import { APPEAR_ANIMATION_OPTIONS } from "@client/lib/jquery/animate";
import { toMilliseconds } from "@client/lib/misc";
import { goTo } from "@client/lib/navigation";
import { IconType } from "@client/types";
import classNames from "classnames";
import * as React from "react";
import { type Purchase } from "../types";

export type Preloader = {
  heading: string;
  playAllSteps: boolean;
  lastStepHeading: string;
  lastStepLongText: string;
  lastStepText: string;
  stepDelay: string;
};

export interface PurchaseModalThirdComponentMapProps {
  canClose: boolean;
  error: Purchase.Api.ErrorCode | null;
  isEditMode: boolean;
  isInProgress: boolean;
  isVisible: boolean;
  preloader: Preloader;
  redirectUrl: string;
  steps: string[];
}
export interface PurchaseModalThirdComponentActionProps {
  onClose: () => void;
}
export type PurchaseModalThirdComponentProps = PurchaseModalThirdComponentMapProps &
  PurchaseModalThirdComponentActionProps;

interface State {
  stepIndex: number;
  redirectingText: string | null;
}

const AnimatedParagraph = animateTag<HTMLParagraphElement>("p");
const animationOptions = { ...APPEAR_ANIMATION_OPTIONS, delay: "100ms" };

export default class extends React.Component<PurchaseModalThirdComponentProps, State> {
  private _interval: any;

  public state: State = {
    stepIndex: -1,
    redirectingText: null
  };

  public componentDidMount() {
    if (this.props.error) return;

    const stepIndex = this.state.stepIndex + 1;
    this.setState({ stepIndex });

    this._interval = setInterval(() => {
      const labelIndex = this.state.stepIndex + 1;
      this.setState({ stepIndex: labelIndex });

      if (labelIndex === this.props.steps.length - 1) {
        clearInterval(this._interval);
        this._interval = null;

        if (this.props.redirectUrl) this._startRedirect();
      }
    }, toMilliseconds(this.props.preloader.stepDelay));
  }

  public componentDidUpdate(
    prevProps: Readonly<PurchaseModalThirdComponentProps>,
    prevState: Readonly<State>,
    snapshot?: any
  ): void {
    if (
      !this.state.redirectingText &&
      this.props.redirectUrl &&
      (!this.props.preloader.playAllSteps || this.state.stepIndex === this.props.steps.length - 1)
    ) {
      clearInterval(this._interval);

      this._startRedirect();
    }
  }

  public render() {
    return (
      <Modal
        className={classNames("last_step_modal with-skin-color-var narrow", `step-3`)}
        hasBackdrop={true}
        isAdminColor={false}
        isDraggable={false}
        isCentered={false}
        isVisible={this.props.isVisible}
        closeBehaviour={this.props.canClose ? undefined : "not-allowed"}
        onClose={this.props.onClose}
        width="100%"
      >
        {this.props.canClose && <Modal.Header title="Trial" onCloseClick={this.props.onClose} />}
        <Modal.Body>
          <div className="content">
            {this.props.error ? (
              this.props.error === "DEPLOYING_WEBSITE" ? (
                <>
                  <div className="icon">
                    <Icon iconClassName={IconType.Tick} />
                  </div>
                  <h4>You're done!</h4>
                  <p>You’ll receive an email as soon as the site is ready</p>
                </>
              ) : (
                <div className="error">
                  We had a trouble processing your request.
                  <br /> Our support team will contact you shortly
                  <br />
                  {this.props.isEditMode && <small>{this.props.error}</small>}
                </div>
              )
            ) : this.props.isInProgress ||
              (this.props.preloader.playAllSteps && this.state.stepIndex < this.props.steps.length - 1) ? (
              <>
                <div className="loading"></div>
                <h4>{this.props.preloader.heading}</h4>
                <AnimatedParagraph animationOptions={animationOptions} key={this.state.stepIndex}>
                  {this.props.steps[this.state.stepIndex]}
                </AnimatedParagraph>
              </>
            ) : !!this.state.redirectingText ? (
              <>
                <div className="loading"></div>
                <h4>{this.props.preloader.lastStepHeading}</h4>
                <p>{this.state.redirectingText}</p>
              </>
            ) : (
              <>
                <div className="loading frozen"></div>
                <h4>Your trial is ready to go</h4>
                <p>Check your inbox for details</p>
                {!!this.props.redirectUrl && (
                  <p>
                    Click <a href={this.props.redirectUrl}>here</a> to proceed
                  </p>
                )}
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  private _startRedirect = () => {
    this.setState({ redirectingText: this.props.preloader.lastStepText });

    const ready = () => {
      goTo(this.props.redirectUrl);

      setTimeout(() => {
        this.setState({ redirectingText: this.props.preloader.lastStepLongText });
      }, 5000);
    };

    let attempts = 0;
    const ping = async () => {
      if (attempts >= 5) {
        ready();
        return;
      }

      const pingUrl = new URL(this.props.redirectUrl);
      try {
        // ensure the site is up. Note, we cannot use the redirect URL here, as it performs additional actions.
        // use base URL, since there's no portal, it should run quick.
        const res = await fetch(pingUrl.origin, {
          headers: {
            "Cache-Control": "no-cache",
            pragma: "no-cache"
          },
          method: "GET"
        });
        if (res.ok) {
          ready();
        } else {
          throw new Error(res.statusText);
        }
      } catch (e) {
        console.error(e);

        setTimeout(ping, 2000);
      } finally {
        attempts++;
      }
    };

    setTimeout(ping, 3000);
  };
}
